import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod'; // Add this if not already imported

export const InputTypes = {
  TEXTBOX: 'textbox',
  DROPDOWN: 'dropdown',
  RADIO: 'radio',
  TEXTAREA: 'textarea',
  DATEPICKER: 'datepicker',
  CHECKBOX: 'checkbox',
  EMAIL: 'email',
  PHONE: 'phone',
  STATE: 'state',
  TIME: 'time'
};

export const DataTypes = {
  TEXT: 'text',
  NUMERIC: 'numeric',
  EMAIL: 'email',
  PHONE: 'phone'
};

export const AlertSeverity = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  SUCCESS: 'success'
};


// Conditional Logic Operators
export const ConditionalOperators = {
  EQUALS: 'equals',
  NOT_EQUALS: 'not_equals',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'not_contains',
  GREATER_THAN: 'greater_than',
  LESS_THAN: 'less_than',
  IS_EMPTY: 'is_empty',
  IS_NOT_EMPTY: 'is_not_empty'
};

// Conditional Actions
export const ConditionalActions = {
  HIDE: 'hide',
  SHOW: 'show',
  ENABLE: 'enable',
  DISABLE: 'disable',
  REQUIRE: 'require',
  SKIP_SECTION: 'skip_section'
};

// Field-Level Conditional Rule
export const createFieldCondition = () => ({
  id: uuidv4(),
  sourceSectionId: '',        // Section containing source field
  sourceFieldName: '',        // Field to watch
  operator: ConditionalOperators.EQUALS,
  triggerValue: '',           // Value to compare against
  action: ConditionalActions.HIDE,
  // Optional: For numeric comparisons
  valueType: 'string' // 'string', 'number', 'boolean'
});

// Section-Level Skip Rule
export const createSectionSkipRule = () => ({
  id: uuidv4(),
  sourceSectionId: '',        // Previous section to check
  sourceFieldName: '',        // Field to check in previous section
  operator: ConditionalOperators.EQUALS,
  triggerValue: '',           // Value that triggers skip
  action: ConditionalActions.SKIP_SECTION,
  // Skip entire section if condition is true
  skipEntireSection: true
});

// Pre-configured states data
export const US_STATES = [
  'Alabama (AL)',
  'Alaska (AK)',
  'Arizona (AZ)',
  'Arkansas (AR)',
  'California (CA)',
  'Colorado (CO)',
  'Connecticut (CT)',
  'Delaware (DE)',
  'Florida (FL)',
  'Georgia (GA)',
  'Hawaii (HI)',
  'Idaho (ID)',
  'Illinois (IL)',
  'Indiana (IN)',
  'Iowa (IA)',
  'Kansas (KS)',
  'Kentucky (KY)',
  'Louisiana (LA)',
  'Maine (ME)',
  'Maryland (MD)',
  'Massachusetts (MA)',
  'Michigan (MI)',
  'Minnesota (MN)',
  'Mississippi (MS)',
  'Missouri (MO)',
  'Montana (MT)',
  'Nebraska (NE)',
  'Nevada (NV)',
  'New Hampshire (NH)',
  'New Jersey (NJ)',
  'New Mexico (NM)',
  'New York (NY)',
  'North Carolina (NC)',
  'North Dakota (ND)',
  'Ohio (OH)',
  'Oklahoma (OK)',
  'Oregon (OR)',
  'Pennsylvania (PA)',
  'Rhode Island (RI)',
  'South Carolina (SC)',
  'South Dakota (SD)',
  'Tennessee (TN)',
  'Texas (TX)',
  'Utah (UT)',
  'Vermont (VT)',
  'Virginia (VA)',
  'Washington (WA)',
  'West Virginia (WV)',
  'Wisconsin (WI)',
  'Wyoming (WY)',
  'District of Columbia (DC)',
  'American Samoa (AS)',
  'Guam (GU)',
  'Northern Mariana Islands (MP)',
  'Puerto Rico (PR)',
  'United States Minor Outlying Islands (UM)',
  'Virgin Islands, U.S. (VI)'
];

// Timezones data
export const TIMEZONES = [
  'UTC', 'EST', 'CST', 'MST', 'PST', 'EDT', 'CDT', 'MDT', 'PDT'
];

// AM/PM options
export const AMPM_OPTIONS = ['AM', 'PM'];

// Hours (1-12) and Minutes (00-59)
export const HOURS = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
export const MINUTES = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

export const createNewField = (type = InputTypes.TEXTBOX) => {
  const baseField = {
    id: uuidv4(),
    name: '',
    label: '',
    description: '',
    type,
    validation: {
      required: false,
      minLength: null,
      maxLength: null,
      minValue: null,
      maxValue: null,
      dataType: DataTypes.TEXT,
      emailValidation: type === InputTypes.EMAIL,
      phoneValidation: type === InputTypes.PHONE,
      timezone: type === InputTypes.TIME ? 'UTC' : undefined
    },
    alertNotes: [],
    options: [],
    placeholder: '',
    // Add conditional visibility rules
    conditions: [], // Array of createFieldCondition()
    // Existing configurations...
    checkboxConfig: type === InputTypes.CHECKBOX ? {
      isSingle: true,
      isMultiple: false,
      defaultChecked: false,
      checkedValue: true,
      uncheckedValue: false
    } : undefined,
    timeConfig: type === InputTypes.TIME ? {
      includeTimezone: true,
      availableTimezones: [...TIMEZONES],
      hourOptions: [...HOURS],
      minuteOptions: [...MINUTES],
      ampmOptions: [...AMPM_OPTIONS]
    } : undefined
  };

  // Update this section to handle CHECKBOX type
  if (type === InputTypes.DROPDOWN || type === InputTypes.RADIO || type === InputTypes.CHECKBOX || type === InputTypes.STATE) {
    if (type === InputTypes.STATE) {
      baseField.options = [...US_STATES];
    } else if (type === InputTypes.CHECKBOX) {
      // For checkbox group, we need options like radio buttons
      baseField.options = ['Option 1', 'Option 2'];
      baseField.checkboxConfig = {
        isSingle: false,           // This is a checkbox group by default
        isMultiple: true,
        defaultChecked: [],        // Array for multiple selections
        checkedValue: 'true',      // For single checkbox
        uncheckedValue: 'false'    // For single checkbox
      };
    } else {
      baseField.options = ['Option 1', 'Option 2'];
    }
  }

  if (type === InputTypes.EMAIL) {
    baseField.placeholder = 'example@domain.com';
    baseField.validation.dataType = DataTypes.EMAIL;
  }

  if (type === InputTypes.PHONE) {
    baseField.placeholder = '(123) 456-7890';
    baseField.validation.dataType = DataTypes.PHONE;
  }

  if (type === InputTypes.STATE) {
    baseField.validation.dataType = DataTypes.TEXT;
  }

  return baseField;
};

export const createNewSection = (order = 0) => ({
  id: uuidv4(),
  title: `Section ${order + 1}`,
  description: '',
  fields: [],
  order,
  // Add section-level skip rules
  skipRules: [], // Array of createSectionSkipRule()
  // Mark if section is skippable (all sections except first)
  isSkippable: order > 0
});

export const createNewWizard = () => ({
  // id: uuidv4(),
  name: 'New Wizard',
  description: '',
  path: '',
  sections: [createNewSection(0)],
  created: new Date(),
  updated: new Date()
});

export const VALIDATION_TYPES = {
  CLIENT_ACCOUNT_NUMBER_EXISTS: 'CLIENT_ACCOUNT_NUMBER_EXISTS',
  LS_ACCOUNT_NUMBER_EXISTS: 'LS_ACCOUNT_NUMBER_EXISTS'
};

export const isServerSideValidation = (validationType) => {
  return [
    VALIDATION_TYPES.CLIENT_ACCOUNT_NUMBER_EXISTS,
    VALIDATION_TYPES.LS_ACCOUNT_NUMBER_EXISTS
  ].includes(validationType);
};

// At the end of your existing types/schema.js file, add ONLY these:
export const SERVER_VALIDATION_TYPES = {
  CLIENT_ACCOUNT_NUMBER_EXISTS: 'CLIENT_ACCOUNT_NUMBER_EXISTS',
  LS_ACCOUNT_NUMBER_EXISTS: 'LS_ACCOUNT_NUMBER_EXISTS'
};
